import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Image as ImageIcon, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Compose() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [storagePath, setStoragePath] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Admin broadcast: admins can also target a specific group for broadcasts only.
  const [adminGroups, setAdminGroups] = useState([]);
  const [groupId, setGroupId] = useState("");

  // Admin broadcast state
  const [mode, setMode] = useState("story"); // story | broadcast
  const [bType, setBType] = useState("course");
  const [bTitle, setBTitle] = useState("");
  const [bBody, setBBody] = useState("");
  const [bCtaLabel, setBCtaLabel] = useState("");
  const [bCtaUrl, setBCtaUrl] = useState("");

  useEffect(() => {
    // Only admins need the group selector (for targeted broadcasts).
    // Regular member stories always go to the Main Feed; group posts are
    // created inside the group page itself.
    if (user?.role !== "admin") return;
    api.get("/groups").then((r) => setAdminGroups(r.data)).catch(() => setAdminGroups([]));
  }, [user?.role]);

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Please choose an image file");
      return;
    }
    setErr("");
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStoragePath(r.data.path);
    } catch (e2) {
      setErr(formatApiError(e2));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const submitStory = async () => {
    if (!storagePath) return;
    setSubmitting(true);
    setErr("");
    try {
      // Stories always go to the main public feed.
      // Per-group posting is handled inside the group page (Posts tab).
      await api.post("/stories", { storage_path: storagePath, caption, group_id: null });
      nav("/feed");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBroadcast = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/broadcasts", {
        type: bType,
        title: bTitle,
        body: bBody,
        image_path: storagePath || null,
        cta_label: bCtaLabel || null,
        cta_url: bCtaUrl || null,
        group_id: groupId || null,
      });
      nav(groupId ? `/groups/${groupId}` : "/feed");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto fade-up">
      <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em] mb-3">Share · 发布</p>
      <h1 className="font-serif text-4xl">A new moment.</h1>
      <p className="text-white/50 mt-2">Your story stays visible for 24 hours, then disappears.</p>

      {user?.role === "admin" && (
        <div className="mt-6 flex gap-2" data-testid="compose-mode-toggle">
          <button
            onClick={() => setMode("story")}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${
              mode === "story" ? "border-[#d4af37] text-[#d4af37]" : "border-white/10 text-white/60"
            }`}
            data-testid="mode-story"
          >
            Story
          </button>
          <button
            onClick={() => setMode("broadcast")}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${
              mode === "broadcast" ? "border-[#d4af37] text-[#d4af37]" : "border-white/10 text-white/60"
            }`}
            data-testid="mode-broadcast"
          >
            Broadcast
          </button>
        </div>
      )}

      {/* Image */}
      <div className="mt-8">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="compose-file-input" />
        {!preview ? (
          <button
            onClick={pickFile}
            className="w-full aspect-[4/5] sm:aspect-[16/10] border border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-3 text-white/40 hover:border-[#d4af37]/60 hover:text-[#d4af37] transition"
            data-testid="compose-pick-image"
          >
            <ImageIcon size={32} strokeWidth={1.2} />
            <span className="text-xs uppercase tracking-[0.25em]">Choose an image</span>
          </button>
        ) : (
          <div className="relative">
            <img src={preview} alt="preview" className="w-full rounded-lg max-h-[60vh] object-contain bg-black" />
            <button
              onClick={() => {
                setPreview(null);
                setStoragePath("");
              }}
              className="absolute top-3 right-3 w-9 h-9 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black"
              data-testid="compose-clear-image"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
            {uploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-[#d4af37] uppercase tracking-widest text-xs">
                Uploading…
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="mt-8 space-y-6">
        {adminGroups.length > 0 && mode === "broadcast" && user?.role === "admin" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              Broadcast to · 广播到
            </label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="editorial-input bg-[#0a0a0a] mt-2"
              data-testid="compose-group-select"
            >
              <option value="">All members · 全员</option>
              {adminGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {mode === "story" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">Caption · 文案</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Say something elegant…"
              className="editorial-input mt-2 resize-none"
              data-testid="compose-caption"
            />
          </div>
        )}

        {mode === "broadcast" && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">Type</label>
              <select className="editorial-input bg-[#0a0a0a] mt-2" value={bType} onChange={(e) => setBType(e.target.value)} data-testid="broadcast-type">
                <option value="course">Course</option>
                <option value="voucher">Voucher</option>
                <option value="announcement">Announcement</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">Title</label>
              <input className="editorial-input mt-2" value={bTitle} onChange={(e) => setBTitle(e.target.value)} data-testid="broadcast-title" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">Body</label>
              <textarea
                value={bBody}
                onChange={(e) => setBBody(e.target.value)}
                rows={4}
                className="editorial-input mt-2 resize-none"
                data-testid="broadcast-body"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">CTA Label</label>
                <input className="editorial-input mt-2" value={bCtaLabel} onChange={(e) => setBCtaLabel(e.target.value)} data-testid="broadcast-cta-label" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">CTA URL</label>
                <input className="editorial-input mt-2" value={bCtaUrl} onChange={(e) => setBCtaUrl(e.target.value)} data-testid="broadcast-cta-url" />
              </div>
            </div>
          </>
        )}
      </div>

      {err && (
        <p className="text-red-400 text-sm mt-6" data-testid="compose-error">
          {err}
        </p>
      )}

      <button
        onClick={mode === "story" ? submitStory : submitBroadcast}
        disabled={
          submitting ||
          (mode === "story" && !storagePath) ||
          (mode === "broadcast" && (!bTitle || !bBody))
        }
        className="mt-10 w-full bg-[#d4af37] text-black py-4 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#f3e5ab] transition disabled:opacity-40 flex items-center justify-center gap-3"
        data-testid="compose-submit"
      >
        <Send size={16} strokeWidth={2} />
        {submitting ? "Posting…" : mode === "story" ? "Publish (24h)" : "Send Broadcast"}
      </button>
    </div>
  );
}
